import { Box, List, ListItem, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'timeago.js';
import { Tooltip } from '~/components/Tooltip';
import { Link, ListItemButtonLink } from '~/components/link';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import type { RecentActivity } from '../queries/recent-activity.generated';

export type AuditLog = NonNullable<
  NonNullable<RecentActivity['network']>['auditLogs']['nodes']
>[number];

export interface LatestAuditLogsProps {
  auditLogs: readonly AuditLog[];
}

export const LatestAuditLogs = ({ auditLogs }: LatestAuditLogsProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const link = useLink();

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="h2" variant="h5">
          {t('pages.dashboard.audit_logs.title')}
        </Typography>
        <Link to={link('/audit-logs')} variant="body1">
          {t('pages.dashboard.view_all')}
        </Link>
      </Box>
      {auditLogs.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t('pages.dashboard.audit_logs.no_activity')}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {auditLogs.map((log) => (
            <ListItem
              key={log.id}
              disablePadding
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <ListItemButtonLink
                to={link(`/audit-logs/${log.id}/details`)}
                disableGutters
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                <Typography variant="body1" sx={{ flex: 1 }}>
                  {log.actionName}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mx: 2 }}>
                  {log.userName}
                </Typography>
                {log.createdAt && (
                  <Tooltip title={formatDate(log.createdAt)}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {format(log.createdAt, i18n.language)}
                    </Typography>
                  </Tooltip>
                )}
              </ListItemButtonLink>
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};
